import React, { useEffect, useState, useRef } from "react";
import Sidebar from "../Usables/Sidebar";
import Header from "../Headers/HeaderProfile";
import Opportunity from "./Opportunity";
import AIMessage from "./AIMessage";
import LatestOpps from "./LatestOpportunities";
import Recommendations from "./Recommendations/index.js";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import "./index.css";
import useAuth from "../../hooks/useAuth.js";
import { TailSpin } from "react-loader-spinner";
import UseAnalyticsTrack from "../../hooks/useAnalytics.js";
import LoadingIndicator from "./LoadingIndicator";

const Opportunities = () => {
  const gaEventTrack = UseAnalyticsTrack("Oppotunities");
  gaEventTrack("Opportunities Open", "Opportunities Page");
  const [oppArr, setOppArr] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null); // pending, completed, failed, etc.
  const [taskId, setTaskId] = useState(null);
  const [taskError, setTaskError] = useState(null);
  const [taskPrompt, setTaskPrompt] = useState(null); // Store the original prompt
  const intervalIdRef = useRef(null); // Use ref to track interval ID
  const { auth } = useAuth();
  const axiosPrivate = useAxiosPrivate(); // Initialize the axiosPrivate instance

  // Function to sanitize error messages to avoid exposing sensitive information
  const getSanitizedErrorMessage = (errorMsg) => {
    if (!errorMsg) return "An error occurred with the AI service";

    // Replace specific error messages that might contain sensitive information
    if (
      errorMsg.includes("ECONNREFUSED") ||
      errorMsg.includes("http://") ||
      errorMsg.includes("https://") ||
      errorMsg.includes("API") ||
      errorMsg.includes("api")
    ) {
      return "Unable to connect to the AI service. Please try again later.";
    }

    return "AI service error. Please try again later.";
  };

  // Function to stop polling
  const stopPolling = () => {
    if (intervalIdRef.current) {
      console.log("Stopping polling interval:", intervalIdRef.current);
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  };

  // Function to check opportunities and task status
  const checkOpportunitiesAndTask = async () => {
    try {
      // First try to get opportunities
      const oppsResponse = await axiosPrivate.get("/api/opportunities");

      console.log("Opportunities API response:", oppsResponse);

      // If we have opportunities, process them and stop checking
      if (
        oppsResponse.data.opportunities &&
        oppsResponse.data.opportunities.length > 0
      ) {
        // Extract all parsedData from all opportunities
        let allOpportunities = [];
        oppsResponse.data.opportunities.forEach((opp) => {
          if (opp.parsedData && Array.isArray(opp.parsedData)) {
            allOpportunities = [...allOpportunities, ...opp.parsedData];
          }
        });

        console.log(
          "Processed opportunities:",
          allOpportunities,
          "Stopping polling"
        );

        setOppArr(allOpportunities);
        setLoading(false);

        // Stop polling once we have opportunities
        stopPolling();
      } else {
        // No opportunities found, check task status
        const taskResponse = await axiosPrivate.get("/api/task-status");

        console.log("Task status response:", taskResponse.data);

        setTaskId(taskResponse.data.task_id);
        setTaskStatus(taskResponse.data.status);
        setTaskError(taskResponse.data.error_message);
        setTaskPrompt(taskResponse.data.prompt); // Store the original prompt

        // If task is completed but we still don't have opportunities, or
        // if task failed, stop loading indicator
        if (
          taskResponse.data.status === "completed" ||
          taskResponse.data.status === "failed"
        ) {
          setLoading(false);
          console.log(
            `Task status is ${taskResponse.data.status}, stopping polling`
          );

          // Stop polling if task is failed or completed
          stopPolling();
        }
      }
    } catch (error) {
      console.error("Error checking opportunities and task:", error);

      // Only stop loading if there's a clear error
      if (error.response && error.response.status !== 404) {
        setError(error.message || "An error occurred");
        setLoading(false);

        console.log("Error occurred, stopping polling");
        // Stop polling on error
        stopPolling();
      }
    }
  };

  // Start polling
  const startPolling = () => {
    // Clean up any existing interval first
    stopPolling();

    console.log("Starting new polling cycle");

    // Call once immediately
    checkOpportunitiesAndTask();

    // Then set up interval
    intervalIdRef.current = setInterval(() => {
      console.log("Polling: checking opportunities and task status");
      checkOpportunitiesAndTask();
    }, 5000);

    console.log("New interval ID:", intervalIdRef.current);
  };

  // Handle retry
  const handleRetry = async () => {
    if (!taskId) {
      setError("No task found to retry");
      return;
    }

    // Show loading indicator immediately
    setLoading(true);
    setError(null);
    setOppArr([]); // Clear any existing opportunities

    try {
      // Use the original prompt from the task, not the error message
      const response = await axiosPrivate.post("/api/retry-opportunities", {
        userID: auth.userID,
        taskId: taskId,
        prompt: taskPrompt, // Use the original prompt
      });

      console.log("Retry response:", response.data);

      // Update task info with new task
      setTaskId(response.data.task_id);
      setTaskStatus("pending");
      setTaskError(null);

      // Keep showing loading indicator
      setLoading(true);

      // Start polling again to check status
      startPolling();
    } catch (error) {
      console.error("Error retrying task:", error);
      setError("Failed to retry. Please try again later.");
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    console.log("Component mounted - starting polling");
    // Start polling on component mount
    startPolling();

    // Clean up on unmount
    return () => {
      console.log("Component unmounting - cleaning up");
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.accessToken]); // Only re-run if auth.accessToken changes

  return (
    <div className="opportunityWrapper">
      <div className="opportunityHeader">
        <Header />
      </div>
      <div className="opportunityBottom">
        <div className="opportunitySidebar">
          <Sidebar />
        </div>
        <div className="opportunityRightUpdated">
          <div className="availableJobs">
            {loading ? (
              <LoadingIndicator message="Loading your personalized opportunities..." />
            ) : taskStatus === "failed" ? (
              <div className="retry-container">
                <div className="error-message">
                  <p>Failed to generate opportunities</p>
                  <p>{getSanitizedErrorMessage(taskError)}</p>
                  <button className="retry-button" onClick={handleRetry}>
                    Retry
                  </button>
                </div>
              </div>
            ) : error ? (
              <div className="error-message">
                <p>Error: {error}</p>
                <p>Please try again later or contact support.</p>
              </div>
            ) : oppArr.length === 0 ? (
              <div className="no-opportunities-message">
                <p>No opportunities found yet.</p>
                <p>Please complete your profile or check back later.</p>
              </div>
            ) : (
              <Recommendations oppArr={oppArr} />
            )}
          </div>
          <div className="aiOpportunities">
            {/* <div className="latestOpportunities">
              <LatestOpps></LatestOpps>
            </div> */}
            <div className="aiMessage">
              <AIMessage />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Opportunities;
